import type { TMetadataValue } from '@/types/index.js';

// "yearBuilt" or "year_built" to "Year built"
export const humaniseKey = (key: string): string => {
	const words = key
		.replace(/([a-z\d])([A-Z])/g, '$1 $2')
		.replace(/[_-]+/g, ' ')
		.trim()
		.toLowerCase();
	return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatValue = (value: TMetadataValue): string | null => {
	if (typeof value === 'string') return value.trim() || null;
	if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString('en-US') : null;
	return null;
};

// A label's text on one line, for where it is not drawn on the canvas: the page's text, the
// thumbnails, search engines. A dataset breaks a long value onto lines of its own with "\n".
export const toOneLine = (text: string): string => text.replace(/\s*\n\s*/g, ' ');

// How many lines a text takes on a label, counting the breaks a dataset put in it
export const countLines = (text: string): number => text.split('\n').length;

/**
 * The lines an item's metadata is shown as, between its name and its measure, in the order the dataset lists
 * the fields: each field's value alone, or for `true`, which has no value to show, its key. A
 * field that is false, null, empty or of another type is left out, so a dataset can keep
 * `"tallest": false` without it showing.
 */
export const getMetadataLines = (metadata: unknown): string[] => {
	if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return [];

	return Object.entries(metadata as Record<string, TMetadataValue>).flatMap(([key, value]) => {
		if (value === true) return [humaniseKey(key)];
		const text = formatValue(value);
		return text === null ? [] : [text];
	});
};
